/**
 * WarehouseToolPicker — find an existing tool to ATTACH on Receive.
 * ─────────────────────────────────────────────────────────────────
 * Rendered conditionally by the Receive page (its own file — not a nested
 * component, not inlined). Backed by the canonical search_inventory endpoint
 * (27-field Tools search + asset tags + part numbers), one site per call.
 *
 * A source-site selector (defaulted to the warehouse site) chooses which site
 * to search — a tool can live at any site, not just the warehouse. On Confirm
 * it returns the tool_id plus the SEARCHED site_id (the tool's own site), which
 * the Receive page drops into linked_tool_id / linked_tool_site_id.
 *
 * onClose is ref-stabilized so ModalShell's browser-back effect (which depends
 * on onClose) never tears down mid-keystroke — the search box stays open while
 * typing regardless of the parent's handler identity.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ModalShell from '../../../shared/components/ModalShell';
import { searchToolsForAttach } from '../api/warehouseApi';

const MIN_Q = 2;
const MATCH_LABELS = { barcode: 'barcode', asset_tag: 'asset tag', part_number: 'part number', qr_code: 'QR code' };

function siteLabel(s) {
  if (s.site_city) return `${s.site_name} (${s.site_city})`;
  return s.site_name || s.site_id;
}

export default function WarehouseToolPicker({ warehouseSiteId, callerEmail, sites, storedToolIds, onConfirm, onCancel }) {
  // storedToolIds keys on the WAREHOUSE site's current occupancy (passed in by
  // the Receive page). It is INDEPENDENT of the source-search site below —
  // "already stored here" always means "already live in this warehouse".
  const stored = storedToolIds || new Set();
  const [selectedSiteId, setSelectedSiteId] = useState(warehouseSiteId || '');
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  // Stable close — see header note.
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const handleClose = useCallback(() => cancelRef.current && cancelRef.current(), []);

  // Debounced search (300ms) on q + selected site.
  useEffect(() => {
    const query = q.trim();
    if (!selectedSiteId || query.length < MIN_Q) {
      setResults([]); setLoading(false); setError(null);
      return undefined;
    }
    setLoading(true); setError(null);
    const handle = setTimeout(() => {
      searchToolsForAttach(callerEmail, selectedSiteId, query)
        .then((rows) => setResults(rows))
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [q, selectedSiteId, callerEmail]);

  // Changing the source site clears any selection from the old site.
  useEffect(() => { setSelected(null); }, [selectedSiteId]);

  const selectedSiteLabel = (() => {
    const s = (sites || []).find((x) => x.site_id === selectedSiteId);
    return s ? siteLabel(s) : selectedSiteId;
  })();

  function confirm() {
    if (!selected) return;
    onConfirm({
      tool_id: selected.tool_id || selected.id,
      site_id: selectedSiteId,
      supplier_tool_id: selected.supplier_tool_id || null,
      tool_description: selected.tool_description || null,
      site_label: selectedSiteLabel,
    });
  }

  const footer = (
    <>
      <button type="button" className="wh-btn-secondary" onClick={handleClose}>Cancel</button>
      <button type="button" className="wh-btn-primary" onClick={confirm} disabled={!selected}>Use this tool</button>
    </>
  );

  const query = q.trim();

  return (
    <ModalShell isOpen onClose={handleClose} title="Find Tool to Attach" footer={footer} className="wh-picker-dialog">
      <label className="wh-field-row">
        <span className="wh-label wh-label-req">Source site</span>
        <select className="wh-input" value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)}>
          {(sites || []).map((s) => (
            <option key={s.site_id} value={s.site_id}>{siteLabel(s)}</option>
          ))}
        </select>
      </label>

      <label className="wh-field-row">
        <span className="wh-label">Search</span>
        <input className="wh-input" type="text" value={q} autoFocus
          onChange={(e) => setQ(e.target.value)} placeholder="Tool ID, description, barcode, part number…" />
      </label>

      <div className="wh-picker-results">
        {query.length < MIN_Q && <div className="wh-empty">Type at least 2 characters to search.</div>}
        {query.length >= MIN_Q && loading && <div className="wh-empty">Searching…</div>}
        {query.length >= MIN_Q && error && <div className="wh-empty wh-error">{error}</div>}
        {query.length >= MIN_Q && !loading && !error && results.length === 0 && (
          <div className="wh-empty">No tools match at {selectedSiteLabel}.</div>
        )}
        {!loading && !error && results.map((row) => {
          const id = row.tool_id || row.id;
          const isStored = stored.has(id);
          const isSel = !isStored && selected && (selected.tool_id || selected.id) === id;
          const mc = row._match_context;
          const mcLabel = mc && MATCH_LABELS[mc.field];
          return (
            <button
              type="button"
              key={id}
              disabled={isStored}
              className={`wh-picker-row${isSel ? ' wh-picker-row-selected' : ''}${isStored ? ' wh-picker-row-disabled' : ''}`}
              onClick={isStored ? undefined : () => setSelected(row)}
            >
              <span className="wh-picker-primary">
                <span className="wh-picker-lbl">Tool ID: </span>{row.supplier_tool_id || '—'}
                {isStored && <span className="wh-picker-stored-tag">already stored here</span>}
              </span>
              <span className="wh-picker-secondary">
                <span className="wh-picker-lbl">Type: </span>{row.tool_type || '—'}
                {' · '}<span className="wh-picker-lbl">OEM: </span>{row.oem || '—'}
                {' · '}<span className="wh-picker-lbl">Desc: </span>{row.tool_description || '—'}
              </span>
              {mcLabel && <span className="wh-picker-match">matched {mcLabel}: {mc.value}</span>}
            </button>
          );
        })}
      </div>
    </ModalShell>
  );
}
